/**
 * Keyed, randomly-addressable checkpoint store for the Copilot chat panel's
 * Revert buttons -- unlike useUndoRedo (a LIFO stack, pop-only), a checkpoint
 * here can be restored by id at any time, even after later turns/phases
 * have added more to the canvas.
 *
 * Serialized protobuf bytes give a true, mutation-safe deep copy of node
 * *data* (same discipline as useUndoRedo), but the round-trip through
 * serializeWorkflow/deserializeWorkflow never carries position -- nodeFromProto
 * unconditionally stamps every deserialized node to (0, 0), since the
 * LBWorkflow protobuf schema doesn't store it. So each checkpoint also
 * captures its own frozen positions (the exact frame usePlanReveal had on
 * screen at that phase boundary) alongside the bytes -- restoring overlays
 * them back on, with no calculateGraphLayout() call, since re-laying-out a
 * checkpoint's partial subgraph in isolation wouldn't reproduce the
 * positions it had as a fragment of the full final-graph layout.
 */

import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { deserializeWorkflow, serializeWorkflow } from '../serialization';
import type { ReactFlowEdge, ReactFlowNode } from '../types';

const MAX_AGENT_TURNS = 8;
const MAX_AGENT_CHECKPOINT_BYTES = 5 * 1024 * 1024;

export interface CheckpointMeta {
  id: string;
  label: string;
  turnId: string;
  phaseIndex: number;
  /** True once this checkpoint's whole completed turn has aged out of the
   * turn/byte cap -- the chat row's undo button disables itself
   * rather than staying clickable and silently no-op'ing. */
  evicted: boolean;
}

interface CheckpointEntry {
  bytes: Uint8Array;
  positions: Map<string, { x: number; y: number }>;
  turnId: string;
  phaseIndex: number;
}

export interface RestoredCheckpoint {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
  positions: Map<string, { x: number; y: number }>;
}

export interface UseAgentCheckpointsResult {
  checkpoints: CheckpointMeta[];
  addCheckpoint: (args: {
    label: string;
    turnId: string;
    phaseIndex: number;
    nodes: ReactFlowNode[];
    edges: ReactFlowEdge[];
  }) => string;
  /** Deserializes the checkpoint's structural bytes and returns them
   * alongside its stored position map -- deserialization is this
   * function's job, not the caller's. Null if the id is unknown or its
   * entry has been evicted. */
  getCheckpoint: (id: string) => RestoredCheckpoint | null;
  /** Refresh node payloads in the most recently captured plan turn after
   * protocol-v2 configuration updates the already-rendered topology. */
  refreshLatestTurnCheckpoints: (nodes: ReactFlowNode[]) => void;
  clear: () => void;
}

export function useAgentCheckpoints(): UseAgentCheckpointsResult {
  const [checkpoints, setCheckpoints] = useState<CheckpointMeta[]>([]);
  const entriesRef = useRef<Map<string, CheckpointEntry>>(new Map());

  const addCheckpoint = useCallback(
    ({
      label,
      turnId,
      phaseIndex,
      nodes,
      edges,
    }: {
      label: string;
      turnId: string;
      phaseIndex: number;
      nodes: ReactFlowNode[];
      edges: ReactFlowEdge[];
    }): string => {
      const id = uuidv4();
      const bytes = serializeWorkflow(nodes, edges);
      const positions = new Map(nodes.map((n) => [n.id, n.position]));
      entriesRef.current.set(id, {
        bytes,
        positions,
        turnId,
        phaseIndex,
      });

      setCheckpoints((prev) => {
        let next = [...prev, { id, label, turnId, phaseIndex, evicted: false }];
        const activeTurnId = turnId;
        const retainedBytes = () =>
          next.reduce(
            (total, meta) =>
              total + (entriesRef.current.get(meta.id)?.bytes.byteLength ?? 0),
            0
          );
        const retainedTurnIds = () => [
          ...new Set(
            next.filter((meta) => !meta.evicted).map((meta) => meta.turnId)
          ),
        ];

        while (
          retainedTurnIds().length > MAX_AGENT_TURNS ||
          retainedBytes() > MAX_AGENT_CHECKPOINT_BYTES
        ) {
          const oldestCompletedTurn = retainedTurnIds().find(
            (candidate) => candidate !== activeTurnId
          );
          // The active turn is retained intact even if it alone exceeds the
          // byte target; partial eviction would make its timeline unsafe.
          if (!oldestCompletedTurn) break;
          next = next.map((meta) => {
            if (meta.turnId !== oldestCompletedTurn || meta.evicted)
              return meta;
            entriesRef.current.delete(meta.id);
            return { ...meta, evicted: true };
          });
        }
        return next;
      });

      return id;
    },
    []
  );

  const getCheckpoint = useCallback((id: string): RestoredCheckpoint | null => {
    const entry = entriesRef.current.get(id);
    if (!entry) return null;
    const { nodes, edges } = deserializeWorkflow(entry.bytes);
    return { nodes, edges, positions: entry.positions };
  }, []);

  const refreshLatestTurnCheckpoints = useCallback((nodes: ReactFlowNode[]) => {
    const entries = [...entriesRef.current.values()];
    let latestTurnId: string | undefined;
    for (let index = entries.length - 1; index >= 0; index -= 1) {
      if (entries[index].phaseIndex >= 0) {
        latestTurnId = entries[index].turnId;
        break;
      }
    }
    if (!latestTurnId) return;

    const configuredById = new Map(nodes.map((node) => [node.id, node]));
    entriesRef.current.forEach((entry) => {
      // The base snapshot must remain the graph before Copilot changed it.
      if (entry.turnId !== latestTurnId || entry.phaseIndex < 0) return;
      const checkpoint = deserializeWorkflow(entry.bytes);
      const refreshedNodes = checkpoint.nodes.map((node) => {
        const configured = configuredById.get(node.id);
        return configured ? { ...configured, position: node.position } : node;
      });
      entry.bytes = serializeWorkflow(refreshedNodes, checkpoint.edges);
    });
  }, []);

  const clear = useCallback(() => {
    entriesRef.current.clear();
    setCheckpoints([]);
  }, []);

  return {
    checkpoints,
    addCheckpoint,
    getCheckpoint,
    refreshLatestTurnCheckpoints,
    clear,
  };
}
